import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { AvaTranslateManager } from '../../utils/AvaTranslate';
import type { AppDispatch, RootState } from '../store';

export type AvaTranslateState = {
  toggleInProgress: boolean;
  guestLanguageSelectorOpen: boolean;

  autoTranslateActive: boolean;
  hostLanguage?: string;
  guestLanguage?: string;
  active?: 'guest' | 'host';

  avaTranslateManager?: AvaTranslateManager;

  sncfConsentGiven: boolean;
  sncfConsentDialogShown: boolean;

  // Simple translation loading state
  translationLoadingState: {
    isLoading: boolean;
    requestedLang: string | null;
    requestTimestamp: number | null;
  };
};

const initialState: AvaTranslateState = {
  toggleInProgress: false,
  guestLanguageSelectorOpen: false,
  autoTranslateActive: false,

  // Consent is implied to start with.
  sncfConsentGiven: true,
  sncfConsentDialogShown: false,

  translationLoadingState: {
    isLoading: false,
    requestedLang: null,
    requestTimestamp: null,
  },
};

export const AvaTranslateSlice = createSlice({
  name: 'avaTranslate',
  initialState,
  reducers: {
    setToggleInProgress(state, { payload }: PayloadAction<boolean>) {
      state.toggleInProgress = payload;
    },
    setAutoTranslateActive(state, { payload }: PayloadAction<boolean>) {
      state.autoTranslateActive = payload;
    },
    setHostLanguage(state, { payload }: PayloadAction<string | undefined>) {
      state.hostLanguage = payload;
    },
    setGuestLanguage(state, { payload }: PayloadAction<string | undefined>) {
      state.guestLanguage = payload;
    },
    setActive(state, { payload }: PayloadAction<'guest' | 'host' | undefined>) {
      state.active = payload;
    },
    setGuestLanguageSelectorOpen(state, { payload }: PayloadAction<boolean>) {
      state.guestLanguageSelectorOpen = payload;
    },
    setSncfConsentGiven(state, { payload }: PayloadAction<boolean>) {
      state.sncfConsentGiven = payload;
    },
    setSncfConsentDialogShown(state, { payload }: PayloadAction<boolean>) {
      state.sncfConsentDialogShown = payload;
    },
    setTranslationLoadingStart(state, { payload }: PayloadAction<{ lang: string; timestamp: number }>) {
      state.translationLoadingState = {
        isLoading: true,
        requestedLang: payload.lang,
        requestTimestamp: payload.timestamp,
      };
    },
    setTranslationLoadingComplete(state) {
      state.translationLoadingState = {
        isLoading: false,
        requestedLang: null,
        requestTimestamp: null,
      };
    },
    setTranslationLoadingTimeout(state) {
      state.translationLoadingState = {
        isLoading: false,
        requestedLang: null,
        requestTimestamp: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      createAvaTranslateManager.fulfilled,
      (state, { payload }: PayloadAction<AvaTranslateManager | undefined>) => {
        if (payload) state.avaTranslateManager = payload;
      }
    );
  },
});

export const AvaTranslateReducer = AvaTranslateSlice.reducer;
export const {
  setToggleInProgress,
  setAutoTranslateActive,
  setActive,
  setHostLanguage,
  setGuestLanguage,
  setGuestLanguageSelectorOpen,
  setSncfConsentGiven,
  setSncfConsentDialogShown,
  setTranslationLoadingStart,
  setTranslationLoadingComplete,
  setTranslationLoadingTimeout,
} = AvaTranslateSlice.actions;

export const createAvaTranslateManager = createAsyncThunk('avaTranslate/createAvaTranslateManager', (_, thunkAPI) => {
  const dispatch = thunkAPI.dispatch as AppDispatch;
  const state = thunkAPI.getState() as RootState;
  const { v1Socket } = state.v1Session;
  if (!v1Socket) return;
  return new AvaTranslateManager(v1Socket, dispatch);
});
